import React, { useEffect, useState } from 'react';
import {
  FormPublicDesignFragment,
  FormPublicFieldFragment,
  FormPublicPageFragment,
} from '../../../graphql/fragment/form.public.fragment';
import { StyledButton } from '@/components/styled/button';
import { Grid, Typography } from '@mui/material';
import { getComponentByType } from '@/components/types';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import parse from 'html-react-parser';

interface Props {
  page?: FormPublicPageFragment;
  design: FormPublicDesignFragment;
  className?: string;
  image?: boolean;
  startPageFields: any;
  setStartPageSubmissions?: any;
  next?: () => void;
  showProgress?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const CustomStartPage: React.FC<Props> = ({
  page,
  design,
  next,
  className,
  showProgress,
  startPageFields,
  setStartPageSubmissions,
}) => {
  const [initialFormValues, setInitialFormValues] = useState<any>({});

  const FieldInput = getComponentByType(startPageFields[0].type);
  const FieldInput2 = getComponentByType(startPageFields[1].type);
  const FieldInput3 = getComponentByType(startPageFields[2].type);
  const FieldInput4 = getComponentByType(startPageFields[3].type);
  const FieldInput5 = getComponentByType(startPageFields[4].type);
  const FieldInput6 = getComponentByType(startPageFields[5].type);
  const FieldInput7 = getComponentByType(startPageFields[6].type);
  const FieldInput8 = getComponentByType(startPageFields[7].type);
  const FieldInput9 = getComponentByType(startPageFields[8].type);
  const FieldInput10 = getComponentByType(startPageFields[9].type);

  const getInitialFieldValues = (fields: [FormPublicFieldFragment]) => {
    let returnObj = {};
    fields.forEach((item) => {
      const fieldData = { [item.id]: JSON.parse(item.defaultValue) };
      returnObj = { ...returnObj, ...fieldData };
    });
    return returnObj;
  };

  const handleSubmit = (values: any, validateForm: any) => {
    if (showProgress !== undefined && next !== undefined) {
      showProgress(true);

      const startPageSubmissions = getStartPageSubmissions(values);

      setStartPageSubmissions(startPageSubmissions);

      validateForm().then((errors: any) => {
        if (Object.keys(errors).length === 0) {
          next();
        }
      });
    }
  };

  const getStartPageSubmissions = (formValues: any) => {
    const keys = Object.keys(formValues);
    const values = Object.values(formValues);
    return keys.map((item, i) => {
      const value = values[i] !== null ? JSON.stringify(values[i]) : '';
      return { field: item, data: value };
    });
  };

  const formValidationSchema = Yup.object().shape({
    ['g1o8Y7']: Yup.string().required('First name required'),
    ['gVYoZ7']: Yup.string().required('Last name required'),
    ['J7ND5V']: Yup.string()
      .email('Invalid email format')
      .required('Email is Required'),
    ['qV8kYj']: Yup.string().required('Country required'),
    ['Q7OA4V']: Yup.string().required('Company required'),
    ['8VPne7']: Yup.boolean().required('Terms of use required'),
    ['J73Gk7']: Yup.boolean().required('Data collection required'),
  });

  const getFieldValue = (values: any, field: any) => {
    const id = field.id;
    return values[id];
  };

  const getFieldError = (errors: any, field: any) => {
    const id = field.id;
    return errors[id];
  };

  const fieldHasError = (errors: any, field: any) => {
    const id = field.id;
    return errors[id] ? true : false;
  };

  useEffect(() => {
    if (startPageFields) {
      const defaultValues = getInitialFieldValues(startPageFields);
      setInitialFormValues(defaultValues);
    }
  }, [startPageFields]);

  return (
    <div className={className}>
      <Grid
        sx={{
          marginTop: '24px',
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'center',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 'calc(100vh - 113px)',
          padding: ['0 24px', '0 24px'],
        }}
      >
        <Grid item>
          <Typography
            sx={{ typography: { sm: 'h4', xs: 'h5' } }}
            style={{ color: design.colors.question }}
          >
            {page?.title}
          </Typography>
        </Grid>
        <Grid item>
          <Typography
            sx={{ mt: 1, mb: 2 }}
            style={{
              color: design.colors.question,
              whiteSpace: 'pre-wrap',
            }}
          >
            {page?.paragraph}
          </Typography>
        </Grid>
        {Object.keys(initialFormValues).length > 0 && (
          <Formik
            validateOnChange={false}
            initialValues={initialFormValues}
            onSubmit={handleSubmit}
            validationSchema={formValidationSchema}
            validateOnBlur={true}
          >
            {({ values, handleChange, errors, validateForm }) => (
              <Form>
                <Grid
                  container
                  direction='row'
                  justifyContent='center'
                  alignItems='center'
                  textAlign='left'
                  spacing={2}
                >
                  {FieldInput2 && (
                    <Grid item xs={4}>
                      <Typography
                        sx={{ typography: { xs: 'h6' } }}
                        style={{ color: design.colors.question }}
                      >
                        {startPageFields[1].title}
                      </Typography>
                      <FieldInput2
                        design={design}
                        field={startPageFields[1]}
                        onChange={handleChange}
                        value={getFieldValue(values, startPageFields[1])}
                        error={getFieldError(errors, startPageFields[1])}
                      />
                    </Grid>
                  )}

                  {FieldInput3 && (
                    <Grid item xs={4}>
                      <Typography
                        sx={{ typography: { xs: 'h6' } }}
                        style={{ color: design.colors.question }}
                      >
                        {startPageFields[2].title}
                      </Typography>
                      <FieldInput3
                        design={design}
                        field={startPageFields[2]}
                        onChange={handleChange}
                        value={getFieldValue(values, startPageFields[2])}
                        error={getFieldError(errors, startPageFields[2])}
                      />
                    </Grid>
                  )}

                  {FieldInput4 && (
                    <Grid item xs={4}>
                      <Typography
                        sx={{ typography: { xs: 'h6' } }}
                        style={{ color: design.colors.question }}
                      >
                        {startPageFields[3].title}
                      </Typography>
                      <FieldInput4
                        design={design}
                        field={startPageFields[3]}
                        onChange={handleChange}
                        value={getFieldValue(values, startPageFields[3])}
                        error={getFieldError(errors, startPageFields[3])}
                      />
                    </Grid>
                  )}
                </Grid>
                <Grid
                  container
                  justifyContent='center'
                  alignItems='center'
                  textAlign='left'
                  spacing={2}
                >
                  {FieldInput5 && (
                    <Grid item xs={4}>
                      <Typography
                        sx={{ typography: { xs: 'h6' } }}
                        style={{ color: design.colors.question }}
                      >
                        {startPageFields[4].title}
                      </Typography>
                      <FieldInput5
                        design={design}
                        field={startPageFields[4]}
                        onChange={handleChange}
                        value={getFieldValue(values, startPageFields[4])}
                        error={getFieldError(errors, startPageFields[4])}
                      />
                    </Grid>
                  )}
                  {FieldInput8 && (
                    <Grid item xs={4}>
                      <Typography
                        sx={{ typography: { xs: 'h6' } }}
                        style={{ color: design.colors.question }}
                      >
                        {startPageFields[7].title}
                      </Typography>
                      <FieldInput8
                        design={design}
                        field={startPageFields[7]}
                        onChange={handleChange}
                        value={getFieldValue(values, startPageFields[7])}
                        error={getFieldError(errors, startPageFields[7])}
                      />
                    </Grid>
                  )}

                  {FieldInput7 && (
                    <Grid item xs={4}>
                      <Typography
                        sx={{ typography: { xs: 'h6' } }}
                        style={{ color: design.colors.question }}
                      >
                        {startPageFields[6].title}
                      </Typography>
                      <FieldInput7
                        design={design}
                        field={startPageFields[6]}
                        onChange={handleChange}
                        value={getFieldValue(values, startPageFields[6])}
                        error={getFieldError(errors, startPageFields[6])}
                      />
                    </Grid>
                  )}
                </Grid>
                <Grid
                  container
                  justifyContent='left'
                  textAlign='left'
                  spacing={2}
                  sx={{ mb: 3 }}
                >
                  {FieldInput6 && (
                    <Grid item xs={4}>
                      <Typography
                        sx={{ typography: { xs: 'h6' } }}
                        style={{ color: design.colors.question }}
                      >
                        {startPageFields[5].title}
                      </Typography>
                      <FieldInput6
                        design={design}
                        field={startPageFields[5]}
                        onChange={handleChange}
                        value={getFieldValue(values, startPageFields[5])}
                        error={getFieldError(errors, startPageFields[5])}
                      />
                    </Grid>
                  )}
                </Grid>
                <Grid
                  container
                  justifyContent='left'
                  textAlign='left'
                  sx={{ mb: 2 }}
                >
                  {FieldInput9 && (
                    <Grid item xs={12}>
                      <Typography
                        sx={{ typography: { xs: 'h6' }, mb: 1 }}
                        style={{ color: design.colors.question }}
                      >
                        {parse(startPageFields[8].title)}
                      </Typography>
                      <FieldInput9
                        design={design}
                        field={startPageFields[8]}
                        onChange={handleChange}
                        value={getFieldValue(values, startPageFields[8])}
                        error={getFieldError(errors, startPageFields[8])}
                      />
                    </Grid>
                  )}
                </Grid>
                <Grid
                  sx={{ mt: 2 }}
                  justifyContent='center'
                  alignItems='center'
                  textAlign='left'
                >
                  {FieldInput10 && (
                    <Grid item xs={4}>
                      <Typography
                        sx={{ typography: { xs: 'h6' }, mb: 1 }}
                        style={{ color: design.colors.question }}
                      >
                        {parse(startPageFields[9].title)}
                      </Typography>
                      <FieldInput10
                        design={design}
                        field={startPageFields[9]}
                        onChange={handleChange}
                        value={getFieldValue(values, startPageFields[9])}
                        error={getFieldError(errors, startPageFields[9])}
                      />
                      {fieldHasError(errors, startPageFields[9]) ? (
                        <Typography
                          sx={{
                            typography: {
                              fontSize: '0.75rem',
                              color: '#e42525',
                            },
                            mb: 1,
                          }}
                        >
                          Terms of use required
                        </Typography>
                      ) : null}
                    </Grid>
                  )}
                </Grid>
                {FieldInput && (
                  <Grid item xs={12} textAlign='left'>
                    <Typography
                      sx={{ typography: { xs: 'h6' }, mb: 1 }}
                      style={{ color: design.colors.question }}
                    >
                      {startPageFields[0].title}
                    </Typography>
                    <FieldInput
                      design={design}
                      field={startPageFields[0]}
                      onChange={handleChange}
                      value={getFieldValue(values, startPageFields[0])}
                      error={getFieldError(errors, startPageFields[0])}
                    />
                    {fieldHasError(errors, startPageFields[0]) ? (
                      <Typography
                        sx={{
                          typography: { fontSize: '0.75rem', color: '#e42525' },
                          mb: 1,
                        }}
                      >
                        Data collection required
                      </Typography>
                    ) : null}
                  </Grid>
                )}
                <StyledButton
                  background={design.colors.button}
                  $buttonColor={design.colors.buttonText}
                  highlight={design.colors.buttonActive}
                  type='submit'
                  className={'continue-button'}
                  onClick={() => handleSubmit(values, validateForm)}
                >
                  {page?.buttonText || 'Get Started'}
                </StyledButton>
              </Form>
            )}
          </Formik>
        )}
      </Grid>
    </div>
  );
};
